import React, { useState } from "react";
import { cash, dateRange, longDate, nightsLabel, parseDate } from "../../services/api";
import CardPay from "./CardPay";

export const STATUS = {
  pending_payment: { label: "Held, awaiting payment", tone: "clay" },
  confirmed: { label: "Confirmed", tone: "forest" },
  requested: { label: "Request received", tone: "clay" },
  cancelled: { label: "Cancelled", tone: "muted" },
  expired: { label: "Hold expired", tone: "muted" },
};

const SCREENING = {
  not_started: "Screening not started",
  in_progress: "Screening in progress",
  approved: "Cleared",
  declined: "Not cleared — we'll be in touch",
  not_required: "No screening needed",
};

/* One row per guest place is stored; the guest reads "Massage × 2". */
const groupAddons = (list) => {
  const m = new Map();
  list.filter((a) => a.status !== "cancelled").forEach((a) => {
    const key = `${a.block_activity_id}:${a.status}:${a.clearance}`;
    m.set(key, { ...a, key, count: (m.get(key)?.count || 0) + 1 });
  });
  return [...m.values()];
};

const holdUntil = (s) => (s ? parseDate(s).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

/**
 * One reservation as its guest sees it: what, where, who, what it cost,
 * what's paid and what's next. Used on the confirmation screen and in the
 * account, so the two never disagree.
 */
const ReservationView = ({ r }) => {
  const [copied, setCopied] = useState(false);
  const live = r.status === "pending_payment" || r.status === "confirmed";
  const copy = () => {
    try { navigator.clipboard.writeText(r.ref); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_) { /* no clipboard */ }
  };

  return (
    <div className="rv">
      <div className="rv-top">
        <div>
          <p className={`rv-status rv-status--${STATUS[r.status]?.tone || "muted"}`}>{STATUS[r.status]?.label || r.status}</p>
          <h2 className="rv-title hFont">{r.retreat_name}</h2>
          <p className="rv-when">{dateRange(r.arrive_date, r.depart_date)} · {nightsLabel(r.nights)}{r.location ? ` · ${r.location}` : ""}</p>
        </div>
        <div className="rv-ref">
          <span>Booking reference</span>
          <b>{r.ref}</b>
          <button type="button" className="bk-link" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
        </div>
      </div>

      {r.status === "pending_payment" && (
        <div className="rv-callout">
          <p className="mb-1"><b>Pay {cash(Math.max(0, r.due_now - r.paid))} to confirm your place.</b> We're holding it until {holdUntil(r.hold_expires_at)}.</p>
          {r.bankInstructions && (
            <>
              <p className="rv-bank">{r.bankInstructions}</p>
              <p className="mb-0">Use <b>{r.ref}</b> as the payment reference.</p>
            </>
          )}
          {r.cardPayments && r.payable?.now > 0
            ? <CardPay r={r} />
            : !r.bankInstructions && <p className="mb-0">We've emailed you how to pay.</p>}
        </div>
      )}
      {r.status === "requested" && (
        <div className="rv-callout">
          <p className="mb-0">We'll check {r.room_type_name} for your dates and come back to you with a price. Nothing is charged and your place isn't held until you confirm.</p>
        </div>
      )}

      <div className="rv-grid">
        <section className="rv-box" aria-label="Stay">
          <h3>Stay</h3>
          <p><b>{r.room_type_name}</b></p>
          <p className="bk-muted">Arrive {longDate(r.arrive_date)}<br />Leave {longDate(r.depart_date)}</p>
          {r.extension_request && <p className="bk-muted">You asked about: {r.extension_request}</p>}
        </section>

        <section className="rv-box" aria-label="Guests">
          <h3>{r.guests.length === 1 ? "Guest" : `${r.guests.length} guests`}</h3>
          <ul className="rv-guests">
            {r.guests.map((g) => (
              <li key={g.id}>
                <b>{g.first_name} {g.last_name}</b>
                <span>{g.diets.map((x) => x.name).join(", ") || "No food preferences given"} · {g.meal_service === "plated" ? "plated meals" : "buffet"}</span>
                {live && <span className={`rv-screen rv-screen--${g.screening_status}`}>{SCREENING[g.screening_status]}</span>}
              </li>
            ))}
          </ul>
        </section>

        {r.addons.length > 0 && (
          <section className="rv-box" aria-label="Add-ons">
            <h3>Add-ons</h3>
            <ul className="bk-mini-list">
              {groupAddons(r.addons).map((a) => (
                <li key={a.key}>
                  {a.name}{a.count > 1 ? ` × ${a.count}` : ""}
                  {a.clearance === "pending" && <span className="bk-muted"> — waiting for medical clearance</span>}
                  {a.status === "requested" && a.clearance !== "pending" && <span className="bk-muted"> — requested</span>}
                  {a.status === "declined" && <span className="bk-muted"> — not available</span>}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <section className="rv-money" aria-label="Payment">
        <h3>Payment</h3>
        <table className="bk-lines">
          <tbody>
            {r.lines.map((l) => <tr key={l.id}><th scope="row">{l.label}</th><td>{l.amount === 0 ? "Included" : cash(l.amount)}</td></tr>)}
          </tbody>
          <tfoot>
            <tr className="bk-total"><th scope="row">Total</th><td>{cash(r.total)}</td></tr>
            <tr><th scope="row">Paid</th><td>{cash(r.paid)}</td></tr>
            {r.balance > 0 && r.status !== "cancelled" && (
              <tr><th scope="row">Balance{r.balance_due_date ? `, due by ${longDate(r.balance_due_date)}` : ""}</th><td>{cash(r.balance)}</td></tr>
            )}
          </tfoot>
        </table>
        {r.installments?.length > 0 && (
          <ul className="rv-plan">
            {r.installments.map((s) => (
              <li key={s.seq} className={s.status === "paid" ? "is-paid" : ""}>
                <span>Instalment {s.seq}, {longDate(s.due_date)}</span><span>{cash(s.amount)}{s.status === "paid" ? " paid" : ""}</span>
              </li>
            ))}
          </ul>
        )}
        {r.status === "confirmed" && <CardPay r={r} />}
      </section>
    </div>
  );
};

export default ReservationView;
